import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { SmartLocationInput } from "../components/SmartLocationInput";
import { TransporeonQuotesPanel } from "../components/TransporeonQuotesPanel";
import { EquipmentSelect, FALLBACK_EQUIPMENT } from "../components/EquipmentSelect";
import {
  Truck,
  RefreshCw,
  MapPin,
  Clock,
  Fuel,
  AlertTriangle,
  Plus,
  X,
  Route,
  Coffee,
  Moon,
  Calculator,
  Info,
  Container,
  PackageOpen,
  Send,
  FileText,
  DollarSign,
  Building2,
  Mail,
  Check,
  Calendar,
  Users,
  TrendingDown,
  TrendingUp,
  Award,
  ChevronRight,
  Star,
  Ban,
  Loader2,
  Download,
  Upload,
  Eye,
  ChevronDown,
  Zap,
  Pencil,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { API_URL, API_BASE_URL } from "../config/api";


const VEHICLE_TYPES = {
  express_van: {
    name: "Express Van",
    description: "Light commercial < 3.5t (no tachograph)",
    icon: "🚐",
    color: "bg-green-500",
  },
  consol_truck: {
    name: "Consol / LTL",
    description: "Part load - pay per pallet/CBM",
    icon: "📦",
    color: "bg-purple-500",
  },
  single_truck: {
    name: "Single Truck (7.5t)",
    description: "Medium goods vehicle",
    icon: "🚚",
    color: "bg-blue-500",
  },
  full_truck: {
    name: "Full Truck (24t)",
    description: "Heavy goods vehicle",
    icon: "🚛",
    color: "bg-orange-500",
  },
  container_fcl: {
    name: "Container FCL",
    description: "20ft/40ft container chassis",
    icon: "📦",
    color: "bg-teal-500",
  },
  articulated_truck: {
    name: "Articulated (40t)",
    description: "Semi-trailer truck",
    icon: "🚛",
    color: "bg-red-500",
  },
};

const CONTAINER_TYPES = [
  { value: "20ft", label: "20ft Standard" },
  { value: "40ft", label: "40ft Standard" },
  { value: "40ft_hc", label: "40ft High Cube" },
];

export default function RouteCalculatorPage() {
  const { user } = useAuth();
  const isCustomer = user?.user_type === "customer";
  const token = localStorage.getItem("token");
  
  const [activeTab, setActiveTab] = useState("calculator");
  
  // Route Calculator state
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [waypoints, setWaypoints] = useState([]);
  const [emptyDropoff, setEmptyDropoff] = useState("");
  const [vehicleType, setVehicleType] = useState("full_truck");
  const [containerType, setContainerType] = useState("40ft");
  const [cargoWeight, setCargoWeight] = useState(10);
  const [departureTime, setDepartureTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Consol/LTL cargo details
  const [consolDetails, setConsolDetails] = useState({
    pallet_count: 1,
    pallet_type: "euro", // euro (120x80cm), industrial (120x100cm), custom
    total_weight_kg: 500,
    total_cbm: 1.5,
    stackable: false,
  });
  
  // Historical rates state for Route Calculator
  const [historicalRates, setHistoricalRates] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [suggestedPrice, setSuggestedPrice] = useState(null);
  
  // Spot Request state
  const [spotForm, setSpotForm] = useState({
    from_location: "",
    customs_location: "",
    to_location: "",
    empty_dropoff: "",
    service_type: "FTL",
    cargo_description: "",
    pickup_date: "",
    notes: "",
    // LTL cargo details
    cargo_weight_kg: "",
    cargo_cbm: "",
    cargo_length_cm: "",
    cargo_width_cm: "",
    cargo_height_cm: "",
    package_type: "pallets",
    package_count: "",
    // Transporeon equipment/unit type + reefer temperature
    equipment_uid: "",
    equipment_name: "",
    commodity: "",
    temp_min: "",
    temp_max: "",
  });
  const [equipmentTypes, setEquipmentTypes] = useState(FALLBACK_EQUIPMENT);
  const [commodities, setCommodities] = useState([]);
  const [matchedCompanies, setMatchedCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [sendingSpot, setSendingSpot] = useState(false);
  const navigate = useNavigate();
  // Transporeon Freight Procurement (runs alongside the email-based flow)
  const [transporeon, setTransporeon] = useState({ configured: false, environment: "sandbox" });
  const [timocom, setTimocom] = useState({ configured: false, environment: "sandbox" });
  const [sendingTp, setSendingTp] = useState(false);
  const [convertingTp, setConvertingTp] = useState(null);
  const [tpOffers, setTpOffers] = useState(null); // { transport_number, kind, from, to, offers, loading }
  // Unified marketplace: post one load to BOTH Transporeon + TimoCom, compare rates
  const [marketplace, setMarketplace] = useState(null); // { transporeon_ref, timocom_ref, timocom_offer_id, providers, rates, status, best, loading, lastUpdated }
  const [postingLoad, setPostingLoad] = useState(false);
  const [convertingRate, setConvertingRate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const marketplaceRef = useRef(null);
  // Historical rates for Spot Request (from trucking companies match)
  const [spotHistoricalRates, setSpotHistoricalRates] = useState([]);
  const [companySortBy, setCompanySortBy] = useState("route_count"); // route_count, avg_price, name
  const [companySortOrder, setCompanySortOrder] = useState("desc");
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  // Tendering state
  const [tenders, setTenders] = useState([]);
  const [showCreateTender, setShowCreateTender] = useState(false);
  const [selectedTender, setSelectedTender] = useState(null);
  const [tenderForm, setTenderForm] = useState({
    name: "",
    description: "",
    deadline: "",
    owner_name: user?.full_name || "",
    owner_email: user?.email || "",
    contract_period: "",
    requirements: "",
    routes: [],  // Will be populated from uploaded template
    invited_companies: "",
    trucking_type_filter: "all",
    process_stages: {
      rfi: { enabled: true, date: "" },
      round_1: { enabled: true, date: "" },
      round_2: { enabled: false, date: "" },
      award: { enabled: true, date: "" }
    }
  });
  const [loadingTenders, setLoadingTenders] = useState(false);
  const [uploadingBid, setUploadingBid] = useState(false);
  const [availableCompaniesForTender, setAvailableCompaniesForTender] = useState([]);
  const [selectedTenderCompanies, setSelectedTenderCompanies] = useState([]);
  const [editTender, setEditTender] = useState(null);
  
  // New: Tender company filters
  const [tenderCompanyFilters, setTenderCompanyFilters] = useState({
    country_loading: "",
    country_delivery: "",
    service_type: "all",
    search_name: "",
  });
  const [loadingTenderCompanies, setLoadingTenderCompanies] = useState(false);
  const [tenderTemplateUploaded, setTenderTemplateUploaded] = useState(false);
  
  // Tender Suggestions
  const [tenderSuggestions, setTenderSuggestions] = useState(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch matched trucking companies based on route and service type
  const fetchMatchedCompanies = useCallback(async (from, to, serviceType) => {
    if (!from || !to) return;
    setLoadingCompanies(true);
    try {
      const params = new URLSearchParams({
        from_location: from,
        to_location: to,
      });
      if (serviceType) {
        params.append("service_type", serviceType);
      }
      
      const response = await axios.get(
        `${API_URL}/trucking-companies/match?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMatchedCompanies(response.data.companies || []);
      setSpotHistoricalRates(response.data.historical_rates || []);
    } catch (error) {
      console.log("Could not fetch matched companies");
    } finally {
      setLoadingCompanies(false);
    }
  }, [token]);

  // Fetch tenders
  const fetchTenders = useCallback(async () => {
    setLoadingTenders(true);
    try {
      const response = await axios.get(`${API_URL}/tenders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTenders(response.data.tenders || []);
    } catch (error) {
      console.log("Could not fetch tenders");
    } finally {
      setLoadingTenders(false);
    }
  }, [token]);

  useEffect(() => {
    if (activeTab === "tendering") {
      fetchTenders();
    }
  }, [activeTab, fetchTenders]);

  // Match companies when spot form route or service type changes
  useEffect(() => {
    if (spotForm.from_location && spotForm.to_location) {
      const timer = setTimeout(() => {
        fetchMatchedCompanies(spotForm.from_location, spotForm.to_location, spotForm.service_type);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [spotForm.from_location, spotForm.to_location, spotForm.service_type, fetchMatchedCompanies]);
  
  // Sort companies
  const sortedCompanies = [...matchedCompanies].sort((a, b) => {
    // Route-matched carriers always float to the top
    const am = a.route_matched ? 1 : 0, bm = b.route_matched ? 1 : 0;
    if (am !== bm) return bm - am;
    let aVal, bVal;
    switch (companySortBy) {
      case "name":
        aVal = a.name?.toLowerCase() || "";
        bVal = b.name?.toLowerCase() || "";
        break;
      case "avg_price":
        aVal = a.avg_price || 0;
        bVal = b.avg_price || 0;
        break;
      case "route_count":
      default:
        aVal = a.route_count || 0;
        bVal = b.route_count || 0;
    }
    if (companySortOrder === "asc") {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  // Fetch companies for tendering based on filters
  const fetchTenderCompanies = useCallback(async () => {
    setLoadingTenderCompanies(true);
    try {
      const params = new URLSearchParams({ 
        from_location: tenderCompanyFilters.country_loading || "Europe",
        to_location: tenderCompanyFilters.country_delivery || "Europe"
      });
      if (tenderCompanyFilters.service_type && tenderCompanyFilters.service_type !== "all") {
        params.append("service_type", tenderCompanyFilters.service_type);
      }
      const response = await axios.get(
        `${API_URL}/trucking-companies/match?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      let companies = response.data.companies || [];
      
      // Client-side filter by name search
      if (tenderCompanyFilters.search_name) {
        const search = tenderCompanyFilters.search_name.toLowerCase();
        companies = companies.filter(c => c.name?.toLowerCase().includes(search));
      }
      
      setAvailableCompaniesForTender(companies);
    } catch (error) {
      console.log("Could not fetch companies for tender");
    } finally {
      setLoadingTenderCompanies(false);
    }
  }, [token, tenderCompanyFilters]);

  // Fetch companies when filters change
  useEffect(() => {
    if (showCreateTender) {
      fetchTenderCompanies();
    }
  }, [showCreateTender, fetchTenderCompanies]);

  // Select all filtered companies
  const selectAllFilteredCompanies = () => {
    const allIds = availableCompaniesForTender.map(c => c.id);
    setSelectedTenderCompanies(allIds);
    toast.success(`Selected ${allIds.length} companies`);
  };
  
  // Clear selection
  const clearCompanySelection = () => {
    setSelectedTenderCompanies([]);
  };

  // Fetch tender suggestions based on historical data
  const fetchTenderSuggestions = useCallback(async () => {
    setLoadingSuggestions(true);
    try {
      const response = await axios.get(
        `${API_URL}/tender-suggestions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTenderSuggestions(response.data);
    } catch (error) {
      console.log("Could not fetch tender suggestions");
      toast.error("Could not load tender suggestions");
    } finally {
      setLoadingSuggestions(false);
    }
  }, [token]);

  // Create tender from suggestion
  const createTenderFromSuggestion = async (suggestion) => {
    const deadline = new Date();
    deadline.setDate(deadline.getDate() + 30); // 30 days from now
    
    try {
      const response = await axios.post(
        `${API_URL}/tender-suggestions/create-from-suggestion`,
        {
          suggestion,
          deadline: deadline.toISOString().split('T')[0],
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowSuggestions(false);
      fetchTenders();
    } catch (error) {
      toast.error("Failed to create tender from suggestion");
    }
  };

  // Toggle company selection for tender
  const toggleTenderCompanySelection = (companyId) => {
    setSelectedTenderCompanies(prev =>
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  // Download tender routes template (user fills this with their requirements)
  const downloadTenderTemplate = () => {
    const headers = [
      "Route ID",
      "Loading Country",
      "Loading City/ZIP",
      "Delivery Country", 
      "Delivery City/ZIP",
      "Service Type (FCL/FTL/LTL/OOG/Express/Reefer/Hazmat)",
      "Equipment (TILT/MEGA/FRIGO/FLATBED)",
      "Frequency (e.g. 3x weekly)",
      "Est. Annual Volume",
      "Est. Weekly Volume",
      "Notes"
    ];
    
    // Add sample rows
    const sampleRows = [
      "1,Slovenia,Koper 6000,Germany,Munich 80331,FTL,TILT,3x weekly,150,3,",
      "2,Slovenia,Koper 6000,Austria,Vienna 1010,FCL,TILT,2x weekly,100,2,",
      "3,Slovenia,Ljubljana 1000,Italy,Milan 20121,LTL,MEGA,1x weekly,50,1,Groupage shipments"
    ];
    
    let csv = headers.join(",") + "\n";
    csv += sampleRows.join("\n") + "\n";
    // Add empty rows for user to fill
    for (let i = 4; i <= 20; i++) {
      csv += `${i},,,,,,,,,,\n`;
    }
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "tender_routes_template.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Tender template downloaded - fill in your routes and upload");
  };

  // Handle tender template upload (populates the tender with routes)
  const handleTenderTemplateUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      const lines = text.split("\n").filter(line => line.trim());
      
      // Skip header row
      const dataLines = lines.slice(1);
      const importedRoutes = dataLines.map(line => {
        const cols = line.split(",").map(s => s.trim());
        // Skip empty rows
        if (!cols[1] && !cols[3]) return null;
        
        return {
          route_id: cols[0] || "",
          loading_country: cols[1] || "",
          loading_city: cols[2] || "",
          delivery_country: cols[3] || "",
          delivery_city: cols[4] || "",
          from: `${cols[2] || ""}, ${cols[1] || ""}`.trim().replace(/^,\s*|,\s*$/g, ""),
          to: `${cols[4] || ""}, ${cols[3] || ""}`.trim().replace(/^,\s*|,\s*$/g, ""),
          service_type: cols[5] || "FTL",
          equipment: cols[6] || "TILT",
          frequency: cols[7] || "",
          annual_volume: cols[8] || "",
          weekly_volume: cols[9] || "",
          notes: cols[10] || ""
        };
      }).filter(r => r !== null && (r.from || r.to));

      if (importedRoutes.length > 0) {
        setTenderForm(prev => ({ ...prev, routes: importedRoutes }));
        setTenderTemplateUploaded(true);
        toast.success(`Imported ${importedRoutes.length} routes from template`);
        
        // Auto-set filters based on imported routes
        const loadingCountries = [...new Set(importedRoutes.map(r => r.loading_country).filter(c => c))];
        const deliveryCountries = [...new Set(importedRoutes.map(r => r.delivery_country).filter(c => c))];
        
        if (loadingCountries.length === 1) {
          setTenderCompanyFilters(prev => ({ ...prev, country_loading: loadingCountries[0] }));
        }
        if (deliveryCountries.length > 0) {
          setTenderCompanyFilters(prev => ({ ...prev, country_delivery: deliveryCountries.join(", ") }));
        }
      } else {
        toast.error("No valid routes found in file");
      }
    };
    reader.readAsText(file);
    // Reset file input
    event.target.value = "";
  };

  // Update process stage
  const updateProcessStage = (stage, field, value) => {
    setTenderForm(prev => ({
      ...prev,
      process_stages: {
        ...prev.process_stages,
        [stage]: {
          ...prev.process_stages[stage],
          [field]: value
        }
      }
    }));
  };

  // Send spot request
  // ── Transporeon + TimoCom: check config once, then expose actions
  useEffect(() => {
    (async () => {
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const { data } = await axios.get(`${API_URL}/transporeon/status`, { headers });
        setTransporeon(data || { configured: false });
      } catch { /* not configured */ }
      // Always try to load equipment/commodity lists so the dropdown is populated.
      try {
        const meta = await axios.get(`${API_URL}/transporeon/metadata`, { headers });
        const units = meta.data?.units || [];
        setEquipmentTypes(units.length ? units : FALLBACK_EQUIPMENT);
        setCommodities((meta.data?.commodities || []).map((n) => ({ uid: n, name: n })));
      } catch {
        setEquipmentTypes(FALLBACK_EQUIPMENT);
      }
      try {
        const { data } = await axios.get(`${API_URL}/timocom/status`, { headers });
        setTimocom(data || { configured: false });
      } catch { /* not configured */ }
    })();
  }, [token]);

  const sendSpotViaTimocom = async () => {
    if (!spotForm.from_location || !spotForm.to_location) {
      toast.error("Please enter from and to locations");
      return;
    }
    if (!timocom.configured) {
      toast.info("TimoCom isn't configured yet. Add your TimoCom API credentials in Settings to publish live offers.");
      return;
    }
    setSendingTp(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/timocom/freight-offer`,
        {
          from_location: spotForm.from_location,
          to_location: spotForm.to_location,
          service_type: spotForm.service_type,
          pickup_date: spotForm.pickup_date || null,
          freight_description: spotForm.cargo_description,
          notes: spotForm.notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Published to TimoCom (${data.internal_ref})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish to TimoCom");
    } finally {
      setSendingTp(false);
    }
  };

  const convertOfferToOrder = async (offer) => {
    if (!tpOffers) return;
    setConvertingTp(offer.offer_id || offer.carrier_creditor_number);
    try {
      const { data } = await axios.post(
        `${API_URL}/marketplace/convert-to-order`,
        {
          provider: "transporeon",
          marketplace_ref: tpOffers.transport_number,
          carrier_creditor_number: offer.carrier_creditor_number || null,
          vendor_name: offer.carrier_name || offer.carrier_creditor_number || "Carrier",
          price: offer.price || 0,
          currency: offer.currency || "EUR",
          loading_place: tpOffers.from || spotForm.from_location || "",
          unloading_place: tpOffers.to || spotForm.to_location || "",
          cargo_description: spotForm.cargo_description || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const closed = data?.close?.closed;
      toast.success(`Transport order ${data.order_number} created${closed ? " · request closed on Transporeon" : ""}`);
      if (!closed && data?.close?.error) {
        toast.info("Order created. Portal close is pending (add Transporeon credentials).");
      }
      setTpOffers(null);
      navigate("/transport-orders");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create transport order");
    } finally {
      setConvertingTp(null);
    }
  };

  const sendSpotViaTransporeon = async () => {
    if (!spotForm.from_location || !spotForm.to_location) {
      toast.error("Please enter from and to locations");
      return;
    }
    if (!transporeon.configured) {
      toast.info("Transporeon isn't configured yet. Add your Transporeon API credentials in Settings to publish live requests.");
      return;
    }
    setSendingTp(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/transporeon/spot-request`,
        {
          from_location: spotForm.from_location,
          to_location: spotForm.to_location,
          service_type: spotForm.service_type,
          cargo_description: spotForm.cargo_description,
          pickup_date: spotForm.pickup_date || null,
          notes: spotForm.notes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Published to Transporeon (${data.transport_number})`);
      setTpOffers({ transport_number: data.transport_number, kind: "spot", from: spotForm.from_location, to: spotForm.to_location, offers: [], loading: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish to Transporeon");
    } finally {
      setSendingTp(false);
    }
  };

  const createTenderViaTransporeon = async () => {
    const firstRoute = (tenderForm.routes && tenderForm.routes[0]) || {};
    if (!tenderForm.name || !tenderForm.deadline) {
      toast.error("Please enter tender name and deadline");
      return;
    }
    if (!firstRoute.from || !firstRoute.to) {
      toast.error("Add at least one route (from/to) for Transporeon");
      return;
    }
    if (!transporeon.configured) {
      toast.info("Transporeon isn't configured yet. Add your Transporeon API credentials in Settings to publish live tenders.");
      return;
    }
    setSendingTp(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/transporeon/tender`,
        {
          name: tenderForm.name,
          from_location: firstRoute.from,
          to_location: firstRoute.to,
          service_type: firstRoute.service_type || "FTL",
          offer_deadline: tenderForm.deadline,
          notes: tenderForm.description,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Tender published to Transporeon (${data.transport_number})`);
      setTpOffers({ transport_number: data.transport_number, kind: "tender", from: firstRoute.from, to: firstRoute.to, offers: [], loading: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to publish tender to Transporeon");
    } finally {
      setSendingTp(false);
    }
  };

  const postLoadToMarketplaces = async () => {
    if (!spotForm.from_location || !spotForm.to_location) {
      toast.error("Please enter from and to locations");
      return;
    }
    if (!transporeon.configured && !timocom.configured) {
      toast.info("No freight platform configured yet. Add Transporeon and/or TimoCom API credentials in Settings to source live truck rates.");
      return;
    }
    setPostingLoad(true);
    try {
      const dims = [spotForm.cargo_length_cm, spotForm.cargo_width_cm, spotForm.cargo_height_cm]
        .every((v) => v)
        ? `${spotForm.cargo_length_cm}x${spotForm.cargo_width_cm}x${spotForm.cargo_height_cm} cm`
        : null;
      const { data } = await axios.post(
        `${API_URL}/marketplace/post-load`,
        {
          from_location: spotForm.from_location,
          to_location: spotForm.to_location,
          service_type: spotForm.service_type,
          cargo_description: spotForm.cargo_description,
          pickup_date: spotForm.pickup_date || null,
          notes: spotForm.notes,
          weight_kg: spotForm.cargo_weight_kg ? Number(spotForm.cargo_weight_kg) : null,
          volume_cbm: spotForm.cargo_cbm ? Number(spotForm.cargo_cbm) : null,
          packages: spotForm.package_count ? Number(spotForm.package_count) : null,
          package_type: spotForm.package_type || null,
          dimensions: dims,
          ldm: spotForm.ldm ? Number(spotForm.ldm) : null,
          stackable: !!spotForm.stackable,
          adr: !!spotForm.adr || /\badr\b|dangerous/i.test(spotForm.cargo_description || ""),
          adr_class: spotForm.adr_class || null,
          commodity: spotForm.commodity || null,
          equipment_uid: spotForm.equipment_uid || null,
          equipment_name: spotForm.equipment_name || null,
          temp_min: spotForm.temp_min !== "" ? Number(spotForm.temp_min) : null,
          temp_max: spotForm.temp_max !== "" ? Number(spotForm.temp_max) : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const posted = data.posted_to || [];
      if (posted.length) {
        toast.success(`Load posted to ${posted.map((p) => (p === "transporeon" ? "Transporeon" : "TimoCom")).join(" & ")}`);
      } else {
        toast.warning("Could not post to any platform yet — add credentials in Settings");
      }
      setMarketplace({
        transporeon_ref: data.transporeon_ref,
        timocom_ref: data.timocom_ref,
        timocom_offer_id: data.timocom_offer_id,
        providers: data.providers || {},
        rates: [],
        status: {},
        loading: false,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to post load to marketplaces");
    } finally {
      setPostingLoad(false);
    }
  };

  const fetchMarketplaceRates = async (silent = false) => {
    const mp = marketplaceRef.current;
    if (!mp) return;
    if (!mp.transporeon_ref && !mp.timocom_ref) return;
    if (!silent) setMarketplace((m) => ({ ...m, loading: true }));
    try {
      const params = new URLSearchParams();
      if (mp.transporeon_ref) params.set("transporeon_ref", mp.transporeon_ref);
      if (mp.timocom_ref) params.set("timocom_ref", mp.timocom_ref);
      if (mp.timocom_offer_id) params.set("timocom_offer_id", mp.timocom_offer_id);
      const { data } = await axios.get(`${API_URL}/marketplace/rates?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMarketplace((m) => (m ? { ...m, rates: data.rates || [], status: data.status || {}, best: data.best || null, loading: false, lastUpdated: new Date() } : m));
      if (!silent && !data.rates?.length) toast.info("No carrier rates yet — refresh again shortly");
    } catch (error) {
      setMarketplace((m) => (m ? { ...m, loading: false } : m));
      if (!silent) toast.error(error.response?.data?.detail || "Failed to pull rates");
    }
  };

  const convertRateToOrder = async (rate) => {
    if (!marketplace) return;
    const key = `${rate.source}-${rate.offer_id || rate.carrier}`;
    setConvertingRate(key);
    try {
      const { data } = await axios.post(
        `${API_URL}/marketplace/convert-to-order`,
        {
          provider: rate.source,
          carrier_creditor_number: rate.carrier_creditor_number || null,
          vendor_name: rate.carrier || "Carrier",
          price: rate.price || 0,
          currency: rate.currency || "EUR",
          loading_place: spotForm.from_location || "",
          unloading_place: spotForm.to_location || "",
          cargo_description: spotForm.cargo_description || null,
          transporeon_ref: marketplace.transporeon_ref || null,
          timocom_ref: marketplace.timocom_ref || null,
          timocom_offer_id: marketplace.timocom_offer_id || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const closed = data?.close || {};
      const closedOn = Object.keys(closed)
        .filter((k) => closed[k]?.closed)
        .map((k) => (k === "transporeon" ? "Transporeon" : "TimoCom"));
      toast.success(`Transport order ${data.order_number} created${closedOn.length ? ` · closed on ${closedOn.join(" & ")}` : ""}`);
      setMarketplace(null);
      navigate("/transport-orders");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create transport order");
    } finally {
      setConvertingRate(null);
    }
  };

  // Keep a live ref of marketplace state for the auto-refresh interval closure.
  useEffect(() => { marketplaceRef.current = marketplace; }, [marketplace]);

  // Auto-refresh rates every 3 min while the dialog is open (polls both platforms).
  useEffect(() => {
    if (!marketplace || !autoRefresh) return;
    if (!marketplace.transporeon_ref && !marketplace.timocom_ref) return;
    const id = setInterval(() => { fetchMarketplaceRates(true); }, 180000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace?.transporeon_ref, marketplace?.timocom_ref, autoRefresh, !!marketplace]);


  // Combined: post to Transporeon/TimoCom AND email selected truck vendors.
  const sendToMarketplacesAndVendors = async () => {
    if (!spotForm.from_location || !spotForm.to_location) {
      toast.error("Please enter from and to locations");
      return;
    }
    await postLoadToMarketplaces();
    if (selectedCompanies.length > 0) {
      await sendSpotRequest();
    } else {
      toast.info("Posted to marketplaces. Select truck vendors to also email them.");
    }
  };

  const sendSpotRequest = async () => {
    if (!spotForm.from_location || !spotForm.to_location) {
      toast.error("Please enter from and to locations");
      return;
    }
    if (selectedCompanies.length === 0) {
      toast.error("Please select at least one trucking company");
      return;
    }
    
    setSendingSpot(true);
    try {
      await axios.post(
        `${API_URL}/spot-requests`,
        {
          ...spotForm,
          trucking_company_ids: selectedCompanies,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Spot request sent to ${selectedCompanies.length} companies`);
      setSpotForm({
        from_location: "",
        customs_location: "",
        to_location: "",
        empty_dropoff: "",
        service_type: "FTL",
        cargo_description: "",
        pickup_date: "",
        notes: "",
      });
      setSelectedCompanies([]);
      setMatchedCompanies([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send spot request");
    } finally {
      setSendingSpot(false);
    }
  };

  // Create tender
  const createTender = async () => {
    if (!tenderForm.name || !tenderForm.deadline) {
      toast.error("Please enter tender name and deadline");
      return;
    }
    
    try {
      // Parse routes
      const routes = tenderForm.routes.map(r => ({
        from_location: r.from,
        to_location: r.to,
        loading_city: r.loading_city || "",
        delivery_city: r.delivery_city || "",
        truck_type: r.equipment || r.truck_type || "",
        service_type: r.service_type,
        frequency: r.frequency,
        volume: r.volume
      }));
      
      // Get selected companies' emails for invitation
      const invitedEmails = availableCompaniesForTender
        .filter(c => selectedTenderCompanies.includes(c.id))
        .map(c => c.email)
        .filter(e => e);
      
      // Also get company names for reference
      const invitedNames = availableCompaniesForTender
        .filter(c => selectedTenderCompanies.includes(c.id))
        .map(c => c.name);
      
      // Build process stages
      const processStages = {
        rfi: { enabled: true, date: tenderForm.process_stages.rfi.date },
        round_1: { enabled: tenderForm.process_stages.round_1.enabled, date: tenderForm.process_stages.round_1.date },
        round_2: { enabled: tenderForm.process_stages.round_2.enabled, date: tenderForm.process_stages.round_2.date },
        award: { enabled: true, date: tenderForm.process_stages.award.date }
      };
      
      const response = await axios.post(
        `${API_URL}/tenders`,
        {
          name: tenderForm.name,
          description: tenderForm.description,
          deadline: tenderForm.deadline,
          owner_name: tenderForm.owner_name || user?.full_name || "Operations",
          owner_email: tenderForm.owner_email || user?.email,
          contract_period: tenderForm.contract_period,
          requirements: tenderForm.requirements,
          routes: routes,
          invited_companies: invitedNames,
          invited_emails: invitedEmails,
          invited_company_ids: selectedTenderCompanies,
          trucking_type: tenderForm.trucking_type_filter,
          process_stages: processStages,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Tender created! ${response.data.invites_sent || selectedTenderCompanies.length} invitation emails sent.`);
      setShowCreateTender(false);
      setSelectedTenderCompanies([]);
      setTenderForm({
        name: "",
        description: "",
        deadline: "",
        owner_name: user?.full_name || "",
        owner_email: user?.email || "",
        contract_period: "",
        requirements: "",
        routes: [{ from: "", to: "", service_type: "FTL", frequency: "", volume: "" }],
        invited_companies: "",
        trucking_type_filter: "all",
        process_stages: {
          rfi: { enabled: true, date: "" },
          round_1: { enabled: true, date: "" },
          round_2: { enabled: false, date: "" },
          award: { enabled: true, date: "" }
        }
      });
      fetchTenders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create tender");
    }
  };

  // Download bid template for existing tender
  const downloadExistingTenderTemplate = async (tenderId) => {
    try {
      const response = await axios.get(
        `${API_URL}/tenders/${tenderId}/download-template`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tender_template_${tenderId.slice(0, 8)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  // Advance tender stage
  const advanceTenderStage = async (tenderId) => {
    try {
      const response = await axios.put(
        `${API_URL}/tenders/${tenderId}/advance-stage`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Tender advanced to: ${response.data.new_stage.replace('_', ' ')}`);
      fetchTenders();
      if (selectedTender?.id === tenderId) {
        // Refresh selected tender
        const updated = await axios.get(`${API_URL}/tenders/${tenderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSelectedTender(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to advance tender");
    }
  };

  const deleteTender = async (tenderId) => {
    if (!window.confirm("Delete this tender permanently? This cannot be undone.")) return;
    try {
      await axios.delete(`${API_URL}/tenders/${tenderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Tender deleted");
      if (selectedTender?.id === tenderId) setSelectedTender(null);
      fetchTenders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete tender");
    }
  };

  const openEditTender = (tender) => {
    setEditTender({
      id: tender.id,
      name: tender.name || "",
      deadline: (tender.deadline || "").slice(0, 10),
      contract_period: tender.contract_period || "",
      description: tender.description || "",
      requirements: tender.requirements || "",
    });
  };

  const saveEditTender = async () => {
    try {
      await axios.put(`${API_URL}/tenders/${editTender.id}`, {
        name: editTender.name,
        deadline: editTender.deadline,
        contract_period: editTender.contract_period,
        description: editTender.description,
        requirements: editTender.requirements,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Tender updated");
      setEditTender(null);
      fetchTenders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update tender");
    }
  };

  const addTenderRoute = () => {
    setTenderForm({
      ...tenderForm,
      routes: [...tenderForm.routes, { from: "", to: "", service_type: "FTL", frequency: "", volume: "" }],
    });
  };

  const updateTenderRoute = (index, field, value) => {
    const updated = [...tenderForm.routes];
    updated[index][field] = value;
    setTenderForm({ ...tenderForm, routes: updated });
  };

  const removeTenderRoute = (index) => {
    setTenderForm({
      ...tenderForm,
      routes: tenderForm.routes.filter((_, i) => i !== index),
    });
  };

  const toggleCompanySelection = (companyId) => {
    setSelectedCompanies(prev => 
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  const allSpotCompaniesSelected = matchedCompanies.length > 0 && selectedCompanies.length >= matchedCompanies.length;
  const toggleSelectAllSpotCompanies = () => {
    if (allSpotCompaniesSelected) setSelectedCompanies([]);
    else setSelectedCompanies(matchedCompanies.map((c) => c.id));
  };

  const addWaypoint = () => {
    setWaypoints([...waypoints, { address: "", stop_duration_hours: 1 }]);
  };

  const removeWaypoint = (index) => {
    setWaypoints(waypoints.filter((_, i) => i !== index));
  };

  const updateWaypoint = (index, field, value) => {
    const updated = [...waypoints];
    updated[index][field] = value;
    setWaypoints(updated);
  };

  const calculateRoute = async () => {
    if (!origin || !destination) {
      toast.error("Please enter origin and destination");
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        origin: { address: origin },
        destination: { address: destination },
        waypoints: waypoints.map((wp) => ({
          address: wp.address,
          stop_duration_hours: parseFloat(wp.stop_duration_hours) || 0,
        })),
        vehicle_type: vehicleType,
        cargo_weight_tons: parseFloat(cargoWeight) || 10,
        departure_time: departureTime || null,
      };

      // Add empty container dropoff for FCL trucks
      if (vehicleType === "container_fcl" && emptyDropoff) {
        requestData.empty_container_dropoff = { address: emptyDropoff };
        requestData.container_type = containerType;
      }
      
      // Add consol/LTL details
      if (vehicleType === "consol_truck") {
        requestData.consol_details = consolDetails;
      }

      const response = await axios.post(`${API_URL}/route/calculate`, requestData);

      setResult(response.data);
      toast.success("Route calculated successfully!");
      
      // Fetch historical rates for price comparison
      fetchHistoricalRates(origin, destination, vehicleType);
    } catch (error) {
      console.error("Route calculation error:", error);
      toast.error(
        error.response?.data?.detail || "Failed to calculate route"
      );
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch historical rates for similar routes (Route Calculator)
  const fetchHistoricalRates = async (from, to, vType) => {
    setLoadingRates(true);
    try {
      const response = await axios.get(`${API_URL}/route/historical-rates`, {
        params: {
          origin: from,
          destination: to,
          vehicle_type: vType,
          limit: 10,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setHistoricalRates(response.data);
      
      // Set suggested price from backend stats
      if (response.data?.stats) {
        const stats = response.data.stats;
        setSuggestedPrice({
          suggested: stats.suggested_price,
          min: stats.minimum,
          max: stats.maximum,
          average: stats.average,
          weighted_average: stats.weighted_average,
          median: stats.median,
          count: stats.count,
        });
      } else {
        setSuggestedPrice(null);
      }
    } catch (error) {
      console.error("Failed to fetch historical rates:", error);
      setHistoricalRates(null);
      setSuggestedPrice(null);
    } finally {
      setLoadingRates(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="bg-gradient-to-r from-orange-600 to-amber-600 rounded-2xl p-6 text-white">
            <div className="flex items-center gap-3 mb-2">
              <Route className="h-8 w-8" />
              <h1 className="text-3xl font-bold">Route & Transit Calculator</h1>
            </div>
            <p className="text-orange-100">
              Calculate routes, request spot rates, and manage tenders
            </p>
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
            <TabsTrigger value="calculator" className="gap-2">
              <Calculator className="h-4 w-4" />
              Route Calculator
            </TabsTrigger>
            <TabsTrigger value="spot" className="gap-2">
              <Send className="h-4 w-4" />
              Spot Request
            </TabsTrigger>
            <TabsTrigger value="tendering" className="gap-2">
              <FileText className="h-4 w-4" />
              Tendering
            </TabsTrigger>
          </TabsList>

          {/* ============ ROUTE CALCULATOR TAB ============ */}
          <TabsContent value="calculator">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Vehicle Selection */}
            <div className="bg-card rounded-xl p-6 border shadow-sm">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Truck className="h-5 w-5 text-primary" />
                Vehicle Type
              </h2>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(VEHICLE_TYPES).map(([key, vehicle]) => (
                  <button
                    key={key}
                    onClick={() => setVehicleType(key)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      vehicleType === key
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">{vehicle.icon}</span>
                      <span className="font-medium text-sm">{vehicle.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {vehicle.description}
                    </p>
                  </button>
                ))}
              </div>
              
              {vehicleType !== "express_van" && vehicleType !== "consol_truck" && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                    <div className="text-sm text-amber-800">
                      <strong>EU Tachograph Rules Apply:</strong>
                      <ul className="mt-1 list-disc list-inside text-xs">
                        <li>Max 4.5h driving before 45min break</li>
                        <li>Max 9h driving per day</li>
                        <li>11h daily rest required</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Consol/LTL Cargo Details */}
              {vehicleType === "consol_truck" && (
                <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-4">
                  <div className="flex items-center gap-2 text-purple-800 font-medium">
                    <PackageOpen className="h-5 w-5" />
                    LTL / Consol Cargo Details
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {/* Pallet Count */}
                    <div>
                      <Label className="text-purple-800">Number of Pallets</Label>
                      <Input
                        type="number"
                        min="1"
                        max="33"
                        value={consolDetails.pallet_count}
                        onChange={(e) => setConsolDetails({...consolDetails, pallet_count: parseInt(e.target.value) || 1})}
                        className="mt-1"
                      />
                    </div>
                    
                    {/* Pallet Type */}
                    <div>
                      <Label className="text-purple-800">Pallet Type</Label>
                      <select
                        value={consolDetails.pallet_type}
                        onChange={(e) => setConsolDetails({...consolDetails, pallet_type: e.target.value})}
                        className="w-full mt-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        <option value="euro">Euro Pallet (120×80cm)</option>
                        <option value="industrial">Industrial (120×100cm)</option>
                        <option value="half">Half Pallet (60×80cm)</option>
                        <option value="custom">Custom Size</option>
                      </select>
                    </div>
                    
                    {/* Total Weight */}
                    <div>
                      <Label className="text-purple-800">Total Weight (kg)</Label>
                      <Input
                        type="number"
                        min="1"
                        value={consolDetails.total_weight_kg}
                        onChange={(e) => setConsolDetails({...consolDetails, total_weight_kg: parseInt(e.target.value) || 0})}
                        className="mt-1"
                        placeholder="e.g., 500"
                      />
                    </div>
                    
                    {/* Total CBM */}
                    <div>
                      <Label className="text-purple-800">Total Volume (CBM)</Label>
                      <Input
                        type="number"
                        min="0.1"
                        step="0.1"
                        value={consolDetails.total_cbm}
                        onChange={(e) => setConsolDetails({...consolDetails, total_cbm: parseFloat(e.target.value) || 0})}
                        className="mt-1"
                        placeholder="e.g., 2.5"
                      />
                    </div>
                  </div>
                  
                  {/* Stackable checkbox */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="stackable"
                      checked={consolDetails.stackable}
                      onChange={(e) => setConsolDetails({...consolDetails, stackable: e.target.checked})}
                      className="rounded"
                    />
                    <Label htmlFor="stackable" className="text-purple-800 cursor-pointer">
                      Cargo is stackable (reduces space needed)
                    </Label>
                  </div>
                  
                  {/* Loading Meters calculation */}
                  <div className="p-3 bg-white rounded border border-purple-200">
                    <div className="text-sm text-purple-900">
                      <strong>Estimated Loading Meters:</strong>{" "}
                      {(() => {
                        const palletWidth = consolDetails.pallet_type === "euro" ? 0.8 : 
                                           consolDetails.pallet_type === "industrial" ? 1.0 :
                                           consolDetails.pallet_type === "half" ? 0.8 : 1.0;
                        const palletLength = consolDetails.pallet_type === "euro" ? 1.2 : 
                                            consolDetails.pallet_type === "industrial" ? 1.2 :
                                            consolDetails.pallet_type === "half" ? 0.6 : 1.2;
                        // 2 pallets side by side for euro, 2 for industrial
                        const palletsPerRow = palletWidth <= 0.85 ? 2 : 2;
                        const rows = Math.ceil(consolDetails.pallet_count / palletsPerRow);
                        const ldm = rows * palletLength * (consolDetails.stackable ? 0.5 : 1);
                        return ldm.toFixed(2);
                      })()}
                      {" "}LDM (of 13.6 max)
                    </div>
                    <div className="text-xs text-purple-600 mt-1">
                      Weight factor: {(consolDetails.total_weight_kg / 1750).toFixed(2)} LDM
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Route Input */}
            <div className="bg-card rounded-xl p-6 border shadow-sm">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Route Details
              </h2>

              <div className="space-y-4">
                {/* Origin */}
                <div>
                  <SmartLocationInput
                    label="Origin (City or Port)"
                    placeholder="Search city or port..."
                    value={{ city: origin }}
                    onChange={(loc) => setOrigin(loc.city || loc.port_name || "")}
                    showSeaport={true}
                    showAirport={false}
                    data-testid="route-calc-origin"
                  />
                </div>

                {/* Waypoints */}
                {waypoints.map((wp, index) => (
                  <div key={index} className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label>Stopover {index + 1}</Label>
                      <div className="relative mt-1">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-500" />
                        <Input
                          placeholder="City or address"
                          value={wp.address}
                          onChange={(e) =>
                            updateWaypoint(index, "address", e.target.value)
                          }
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div className="w-24">
                      <Label>Stop (hrs)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={wp.stop_duration_hours}
                        onChange={(e) =>
                          updateWaypoint(index, "stop_duration_hours", e.target.value)
                        }
                        className="mt-1"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeWaypoint(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={addWaypoint}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Stopover
                </Button>

                {/* Destination */}
                <div>
                  <SmartLocationInput
                    label="Destination (City or Port)"
                    placeholder="Search city or port..."
                    value={{ city: destination }}
                    onChange={(loc) => setDestination(loc.city || loc.port_name || "")}
                    showSeaport={true}
                    showAirport={false}
                    data-testid="route-calc-destination"
                  />
                </div>

                {/* Empty Container Drop-off - Only for FCL trucks */}
                {vehicleType === "container_fcl" && (
                  <>
                    <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <PackageOpen className="h-4 w-4 text-teal-600" />
                        <span className="text-sm font-medium text-teal-800">Container Options</span>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <Label className="text-teal-700">Container Type</Label>
                          <select
                            value={containerType}
                            onChange={(e) => setContainerType(e.target.value)}
                            className="w-full mt-1 px-3 py-2 bg-white border border-teal-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          >
                            {CONTAINER_TYPES.map((ct) => (
                              <option key={ct.value} value={ct.value}>
                                {ct.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-teal-700">Empty Container Drop-off (optional)</Label>
                          <div className="relative mt-1">
                            <PackageOpen className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-teal-500" />
                            <Input
                              placeholder="e.g., Port of Koper"
                              value={emptyDropoff}
                              onChange={(e) => setEmptyDropoff(e.target.value)}
                              className="pl-10 border-teal-200"
                            />
                          </div>
                          <p className="text-xs text-teal-600 mt-1">
                            Where to return the empty container after delivery
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Cargo Weight */}
                <div>
                  <Label htmlFor="weight">Cargo Weight (tons)</Label>
                  <Input
                    id="weight"
                    type="number"
                    min="0"
                    step="0.5"
                    value={cargoWeight}
                    onChange={(e) => setCargoWeight(e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Departure Time */}
                <div>
                  <Label htmlFor="departure">Departure Time (optional)</Label>
                  <Input
                    id="departure"
                    type="datetime-local"
                    value={departureTime}
                    onChange={(e) => setDepartureTime(e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>

              <Button
                onClick={calculateRoute}
                disabled={loading}
                className="w-full mt-6 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Calculator className="h-4 w-4 mr-2" />
                    Calculate Route
                  </>
                )}
              </Button>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {result ? (
              <div className="space-y-4">
                {/* Summary Card */}
                <div className="bg-card rounded-xl p-6 border shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">Route Summary</h2>
                    <div className="flex gap-2">
                      {result.container_type && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
                          {result.container_type}
                        </span>
                      )}
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium text-white ${
                          VEHICLE_TYPES[result.vehicle_type]?.color || "bg-gray-500"
                        }`}
                      >
                        {result.vehicle_name}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground mb-4">
                    <MapPin className="h-4 w-4 text-green-500" />
                    <span className="font-medium">{result.origin}</span>
                    {result.waypoints?.length > 0 && (
                      <>
                        <span>→</span>
                        <span className="text-blue-500">
                          {result.waypoints.length} stop(s)
                        </span>
                      </>
                    )}
                    <span>→</span>
                    <MapPin className="h-4 w-4 text-red-500" />
                    <span className="font-medium">{result.destination}</span>
                    {result.empty_container_dropoff && (
                      <>
                        <span>→</span>
                        <PackageOpen className="h-4 w-4 text-teal-500" />
                        <span className="font-medium text-teal-600">{result.empty_container_dropoff}</span>
                        <span className="text-xs text-teal-500">(empty return)</span>
                      </>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <Route className="h-6 w-6 mx-auto text-primary mb-2" />
                      <div className="text-2xl font-bold text-primary">
                        {result.total_distance_km} km
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Total Distance
                      </div>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 text-center">
                      <Clock className="h-6 w-6 mx-auto text-orange-500 mb-2" />
                      <div className="text-2xl font-bold text-orange-500">
                        {result.total_time_formatted}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Total Transit Time
                      </div>
                    </div>
                  </div>

                  {result.estimated_arrival && (
                    <div className="mt-4 p-3 bg-green-50 rounded-lg text-center">
                      <div className="text-sm text-green-700">
                        Estimated Arrival
                      </div>
                      <div className="font-semibold text-green-800">
                        {new Date(result.estimated_arrival).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>

                {/* Tachograph Details */}
                {result.tachograph_required && (
                  <div className="bg-card rounded-xl p-6 border shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-amber-500" />
                      Tachograph Schedule
                    </h3>

                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="text-lg font-bold text-blue-600">
                          {result.tachograph_summary.driving_hours}h
                        </div>
                        <div className="text-xs text-blue-700">Pure Driving</div>
                      </div>
                      <div className="text-center p-3 bg-amber-50 rounded-lg">
                        <div className="text-lg font-bold text-amber-600">
                          {result.tachograph_summary.break_hours}h
                        </div>
                        <div className="text-xs text-amber-700">Breaks</div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <div className="text-lg font-bold text-purple-600">
                          {result.tachograph_summary.driving_days}
                        </div>
                        <div className="text-xs text-purple-700">Driving Days</div>
                      </div>
                    </div>

                    {/* Breaks Timeline */}
                    {result.tachograph_summary.breaks?.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium flex items-center gap-2">
                          <Coffee className="h-4 w-4" />
                          Required Breaks
                        </h4>
                        {result.tachograph_summary.breaks.map((brk, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg text-sm"
                          >
                            <div className="w-20 text-muted-foreground">
                              After {brk.after_km} km
                            </div>
                            <div className="flex-1">{brk.description}</div>
                            <div className="font-medium text-amber-600">
                              {brk.duration_hours * 60} min
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Daily Rests */}
                    {result.tachograph_summary.daily_rests?.length > 0 && (
                      <div className="space-y-2 mt-4">
                        <h4 className="text-sm font-medium flex items-center gap-2">
                          <Moon className="h-4 w-4" />
                          Daily Rest Periods
                        </h4>
                        {result.tachograph_summary.daily_rests.map((rest, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-3 p-2 bg-purple-50 rounded-lg text-sm"
                          >
                            <div className="w-20 text-muted-foreground">
                              Day {rest.after_day}
                            </div>
                            <div className="flex-1">{rest.description}</div>
                            <div className="font-medium text-purple-600">
                              {rest.duration_hours}h rest
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Cost Estimate - Hidden for customers */}
                {!isCustomer && result.cost_estimate && (
                <div className="bg-card rounded-xl p-6 border shadow-sm">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Fuel className="h-5 w-5 text-green-500" />
                    Cost Estimate
                  </h3>

                  <div className="space-y-3">
                    {/* Fuel Details */}
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-2 font-medium">FUEL DETAILS</div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Consumption:</span>
                          <span className="font-medium ml-1">{result.cost_estimate.fuel_liters}L</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Diesel:</span>
                          <span className="font-medium ml-1">€{result.cost_estimate.fuel_price_eur_per_liter}/L</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Fuel cost:</span>
                          <span className="font-medium ml-1">€{result.cost_estimate.fuel_cost_eur}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* FTL Pricing Breakdown */}
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-xs text-green-700 mb-2 font-medium">FTL PRICING BREAKDOWN</div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-green-700">Base transport rate</span>
                          <span className="font-medium">
                            €{result.cost_estimate.cost_per_km_eur}/km × {result.total_distance_km}km = €{result.cost_estimate.ftl_base_cost || (result.total_distance_km * result.cost_estimate.cost_per_km_eur).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-green-700">Fuel surcharge ({result.cost_estimate.ftl_fuel_surcharge_percent || result.cost_estimate.pricing_config?.fuel_surcharge_percent || 25}%)</span>
                          <span className="font-medium text-amber-700">
                            + €{result.cost_estimate.ftl_fuel_surcharge || ((result.total_distance_km * result.cost_estimate.cost_per_km_eur) * 0.25).toFixed(2)}
                          </span>
                        </div>
                        <hr className="border-green-200" />
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-green-800">Total FTL Price</span>
                          <span className="font-bold text-green-700 text-lg">
                            €{result.cost_estimate.total_cost_eur}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* LTL/Consol Pricing Breakdown */}
                    {vehicleType === "consol_truck" && consolDetails.pallet_count > 0 && (
                      <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <h4 className="font-medium text-purple-800 flex items-center gap-2 mb-3">
                          <PackageOpen className="h-4 w-4" />
                          LTL / Part Load Pricing
                        </h4>
                        
                        {(() => {
                          // Get pricing config from backend or use defaults
                          const pricingConfig = result.cost_estimate.pricing_config || {};
                          const handlingCost = pricingConfig.handling_cost || 35;
                          const marginPercent = pricingConfig.margin_percent || 15;
                          const minCharge = pricingConfig.min_charge || 150;
                          const kgPerLdm = pricingConfig.kg_per_ldm || 1850;
                          const maxTruckLdm = pricingConfig.max_truck_ldm || 13.6;
                          const ltlBaseRate = pricingConfig.ltl_base_rate_per_km || 1.85;
                          const ftlBaseRate = pricingConfig.ftl_base_rate_per_km || 1.35;
                          
                          // Calculate loading meters
                          const palletWidth = consolDetails.pallet_type === "euro" ? 0.8 : 
                                             consolDetails.pallet_type === "industrial" ? 1.0 :
                                             consolDetails.pallet_type === "half" ? 0.8 : 1.0;
                          const palletLength = consolDetails.pallet_type === "euro" ? 1.2 : 
                                              consolDetails.pallet_type === "industrial" ? 1.2 :
                                              consolDetails.pallet_type === "half" ? 0.6 : 1.2;
                          const palletsPerRow = palletWidth <= 0.85 ? 2 : 2;
                          const rows = Math.ceil(consolDetails.pallet_count / palletsPerRow);
                          const spaceLdm = rows * palletLength * (consolDetails.stackable ? 0.5 : 1);
                          const weightLdm = consolDetails.total_weight_kg / kgPerLdm;
                          const actualLdm = Math.max(spaceLdm, weightLdm);
                          
                          // Calculate LTL pricing using HIGHER LTL rate (€1.80-1.90/km range)
                          // Use ltl_total_cost_eur from backend which uses the LTL rate
                          const ltlFullTruckPrice = result.cost_estimate.ltl_total_cost_eur || result.cost_estimate.total_cost_eur;
                          const pricePerLdm = ltlFullTruckPrice / maxTruckLdm;
                          const transportShare = pricePerLdm * actualLdm;
                          const withHandling = transportShare + handlingCost;
                          const withMargin = withHandling * (1 + marginPercent / 100);
                          const ltlPrice = Math.max(Math.round(withMargin), minCharge);
                          const pricePerPallet = Math.round(ltlPrice / consolDetails.pallet_count);
                          const pricePerCbm = consolDetails.total_cbm > 0 ? Math.round(ltlPrice / consolDetails.total_cbm) : 0;
                          
                          return (
                            <div className="space-y-3">
                              {/* Main Price Cards */}
                              <div className="grid grid-cols-2 gap-3">
                                <div className="bg-white rounded p-3 text-center border border-purple-200">
                                  <div className="text-2xl font-bold text-purple-700">€{ltlPrice}</div>
                                  <div className="text-xs text-purple-600">Estimated LTL Price</div>
                                  <div className="text-xs text-muted-foreground">({actualLdm.toFixed(2)} LDM)</div>
                                </div>
                                <div className="bg-white rounded p-3 text-center border border-indigo-200">
                                  <div className="text-2xl font-bold text-indigo-700">€{pricePerPallet}</div>
                                  <div className="text-xs text-indigo-600">Per Pallet</div>
                                  <div className="text-xs text-muted-foreground">({consolDetails.pallet_count} pallets)</div>
                                </div>
                              </div>
                              
                              {/* Unit Rates */}
                              <div className="grid grid-cols-3 gap-2 text-sm">
                                <div className="bg-white/50 rounded p-2 text-center">
                                  <div className="font-semibold text-purple-700">€{Math.round(pricePerLdm)}</div>
                                  <div className="text-xs text-purple-600">per LDM</div>
                                </div>
                                {pricePerCbm > 0 && (
                                  <div className="bg-white/50 rounded p-2 text-center">
                                    <div className="font-semibold text-purple-700">€{pricePerCbm}</div>
                                    <div className="text-xs text-purple-600">per CBM</div>
                                  </div>
                                )}
                                <div className="bg-white/50 rounded p-2 text-center">
                                  <div className="font-semibold text-purple-700">€{consolDetails.total_weight_kg > 0 ? Math.round(ltlPrice / (consolDetails.total_weight_kg / 1000)) : 0}</div>
                                  <div className="text-xs text-purple-600">per ton</div>
                                </div>
                              </div>
                              
                              {/* Detailed Calculation Breakdown */}
                              <div className="bg-purple-100/50 rounded p-3 space-y-1 text-xs">
                                <div className="font-medium text-purple-800 mb-2">CALCULATION BREAKDOWN (LTL Rate: €{ltlBaseRate}/km):</div>
                                <div className="flex justify-between text-purple-700">
                                  <span>1. LTL Base Price (€{ltlBaseRate}/km × {result.total_distance_km}km + {pricingConfig.fuel_surcharge_percent || 25}% fuel)</span>
                                  <span>= €{Math.round(ltlFullTruckPrice)}</span>
                                </div>
                                <div className="flex justify-between text-purple-700">
                                  <span>2. €{Math.round(ltlFullTruckPrice)} ÷ {maxTruckLdm} LDM = €{Math.round(pricePerLdm)}/LDM</span>
                                </div>
                                <div className="flex justify-between text-purple-700">
                                  <span>3. €{Math.round(pricePerLdm)}/LDM × {actualLdm.toFixed(2)} LDM</span>
                                  <span>= €{Math.round(transportShare)}</span>
                                </div>
                                <div className="flex justify-between text-purple-700">
                                  <span>4. + Handling fee</span>
                                  <span>+ €{handlingCost}</span>
                                </div>
                                <div className="flex justify-between text-purple-700">
                                  <span>5. + Margin ({marginPercent}%)</span>
                                  <span>× {(1 + marginPercent / 100).toFixed(2)}</span>
                                </div>
                                {ltlPrice === minCharge && (
                                  <div className="flex justify-between text-amber-700 font-medium">
                                    <span>6. Min. charge applied</span>
                                    <span>€{minCharge}</span>
                                  </div>
                                )}
                                <hr className="border-purple-300 my-1" />
                                <div className="flex justify-between font-medium text-purple-800">
                                  <span>Total LTL Price</span>
                                  <span>€{ltlPrice}</span>
                                </div>
                              </div>
                              
                              {/* Rate Info Note */}
                              <div className="text-xs text-purple-700 bg-white/50 p-2 rounded">
                                <strong>Rate info:</strong> FTL uses €{ftlBaseRate}/km, LTL uses €{ltlBaseRate}/km (higher rate covers consolidation costs).
                                <br />
                                <strong>LDM:</strong> Using higher of space ({spaceLdm.toFixed(2)} LDM) 
                                or weight ({weightLdm.toFixed(2)} LDM @ {kgPerLdm} kg/LDM).
                                {consolDetails.stackable ? " Stackable discount applied." : ""}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
                )}

                {/* Historical Rates & Suggested Price - shown after route calculation */}
                {!isCustomer && (loadingRates || suggestedPrice || historicalRates?.rates?.length > 0) && (
                  <div className="bg-card rounded-xl p-6 border shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-purple-500" />
                      Historical Rate Analysis
                      {loadingRates && <Loader2 className="h-4 w-4 animate-spin ml-2" />}
                    </h3>
                    
                    {suggestedPrice && (
                      <div className="mb-4">
                        <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-lg p-4 text-center mb-3">
                          <div className="text-xs text-purple-600 mb-1">SUGGESTED PRICE (weighted average)</div>
                          <div className="text-3xl font-bold text-purple-700">€{suggestedPrice.suggested}</div>
                          <div className="text-xs text-purple-500 mt-1">
                            Based on {suggestedPrice.count} similar route{suggestedPrice.count !== 1 ? 's' : ''}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2 text-center text-sm">
                          <div className="bg-green-50 rounded p-2">
                            <div className="text-green-600 font-semibold">€{suggestedPrice.min}</div>
                            <div className="text-xs text-green-700">Minimum</div>
                          </div>
                          <div className="bg-blue-50 rounded p-2">
                            <div className="text-blue-600 font-semibold">€{suggestedPrice.median || suggestedPrice.average}</div>
                            <div className="text-xs text-blue-700">Median</div>
                          </div>
                          <div className="bg-red-50 rounded p-2">
                            <div className="text-red-600 font-semibold">€{suggestedPrice.max}</div>
                            <div className="text-xs text-red-700">Maximum</div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Recent historical rates list */}
                    {historicalRates?.rates?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Recent Similar Routes ({historicalRates.rates.length})
                        </h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {historicalRates.rates.map((rate, idx) => (
                            <div key={idx} className="flex justify-between items-center text-sm bg-slate-50 p-2 rounded">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded">
                                    {Math.round(rate.route_match_score * 100)}% match
                                  </span>
                                  <span className="font-medium truncate">{rate.trucking_company}</span>
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {rate.from} → {rate.to}
                                </div>
                              </div>
                              <div className="text-right ml-2">
                                <div className="font-semibold text-green-700">€{rate.price}</div>
                                {rate.date && (
                                  <div className="text-xs text-muted-foreground">
                                    {new Date(rate.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {!loadingRates && !historicalRates?.rates?.length && (
                      <div className="text-center py-4 text-muted-foreground">
                        <TrendingDown className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No historical rates found for this route</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Customer Notice - For rates contact us */}
                {isCustomer && (
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                  <h4 className="font-medium text-blue-800 flex items-center gap-2 mb-2">
                    <Info className="h-4 w-4" />
                    Need a Rate?
                  </h4>
                  <p className="text-sm text-blue-700">
                    For transport rates and quotes, please contact our sales team or submit a quote request.
                    Email: <a href="mailto:sales@europacific.com" className="underline">sales@europacific.com</a>
                  </p>
                </div>
                )}

                {/* Notes */}
                {result.notes?.length > 0 && (
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <h4 className="font-medium text-blue-800 flex items-center gap-2 mb-2">
                      <Info className="h-4 w-4" />
                      Important Notes
                    </h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      {result.notes.map((note, idx) => (
                        <li key={idx}>• {note}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-card rounded-xl p-12 border shadow-sm text-center">
                <Route className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground mb-2">
                  No Route Calculated
                </h3>
                <p className="text-sm text-muted-foreground">
                  Enter origin and destination to calculate transit time with EU tachograph rules
                </p>
              </div>
            )}
          </motion.div>
        </div>
          </TabsContent>

          {/* ============ SPOT REQUEST TAB ============ */}
          <TabsContent value="spot">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Spot Request Form */}
              <div className="space-y-4">
                <div className="bg-card rounded-xl p-6 border shadow-sm">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Send className="h-5 w-5 text-blue-600" />
                    Spot Rate Request
                  </h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    Send rate requests to trucking companies for a specific route
                  </p>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>From Location *</Label>
                        <SmartLocationInput
                          value={{ city: spotForm.from_location }}
                          onChange={(loc) => setSpotForm({ ...spotForm, from_location: loc.city || loc.port_name || "" })}
                          placeholder="Search city or port..."
                          showSeaport={true}
                          data-testid="spot-req-from"
                        />
                      </div>
                      <div>
                        <Label>Customs (optional)</Label>
                        <Input
                          value={spotForm.customs_location}
                          onChange={(e) => setSpotForm({ ...spotForm, customs_location: e.target.value })}
                          placeholder="e.g., Ljubljana Customs"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>To Location *</Label>
                        <SmartLocationInput
                          value={{ city: spotForm.to_location }}
                          onChange={(loc) => setSpotForm({ ...spotForm, to_location: loc.city || loc.port_name || "" })}
                          placeholder="Search city or port..."
                          showSeaport={true}
                          data-testid="spot-req-to"
                        />
                      </div>
                      <div>
                        <Label>Empty Container Dropoff (optional)</Label>
                        <Input
                          value={spotForm.empty_dropoff}
                          onChange={(e) => setSpotForm({ ...spotForm, empty_dropoff: e.target.value })}
                          placeholder="e.g., Koper Port"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Service Type</Label>
                        <select
                          value={spotForm.service_type}
                          onChange={(e) => setSpotForm({ ...spotForm, service_type: e.target.value })}
                          className="w-full h-10 rounded-md border border-input bg-background px-3"
                        >
                          <option value="FCL">FCL (Full Container Load)</option>
                          <option value="FTL">FTL (Full Truck Load)</option>
                          <option value="LTL">LTL (Less Than Truck Load)</option>
                          <option value="Express">Express</option>
                        </select>
                      </div>
                      <div>
                        <Label>Pickup Date</Label>
                        <Input
                          type="date"
                          value={spotForm.pickup_date}
                          onChange={(e) => setSpotForm({ ...spotForm, pickup_date: e.target.value })}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Cargo Description</Label>
                      <Input
                        value={spotForm.cargo_description}
                        onChange={(e) => setSpotForm({ ...spotForm, cargo_description: e.target.value })}
                        placeholder="e.g., 40ft HC container, 18t"
                      />
                    </div>

                    {/* Shipment Details — full quote data sent to Transporeon (always shown) */}
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-4">
                      <h4 className="font-medium text-amber-800 flex items-center gap-2">
                        <PackageOpen className="h-4 w-4" />
                        Shipment Details
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-sm">Container / Truck / Unit Type *</Label>
                          <EquipmentSelect
                            options={equipmentTypes}
                            value={spotForm.equipment_uid}
                            onChange={(uid, name) => setSpotForm({ ...spotForm, equipment_uid: uid, equipment_name: name })}
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Type of goods</Label>
                          <EquipmentSelect
                            options={commodities}
                            value={spotForm.commodity}
                            onChange={(uid) => setSpotForm({ ...spotForm, commodity: uid })}
                            placeholder="Select commodity"
                            icon={PackageOpen}
                            testIdPrefix="commodity"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Package Type</Label>
                          <select
                            value={spotForm.package_type}
                            onChange={(e) => setSpotForm({ ...spotForm, package_type: e.target.value })}
                            className="w-full h-10 rounded-md border border-input bg-background px-2 text-sm"
                          >
                            <option value="pallets">Pallets</option>
                            <option value="boxes">Boxes/Cartons</option>
                            <option value="crates">Crates</option>
                            <option value="drums">Drums</option>
                            <option value="bags">Bags/Sacks</option>
                            <option value="rolls">Rolls</option>
                            <option value="bundles">Bundles</option>
                            <option value="loose">Loose Cargo</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-sm">Quantity / Packages</Label>
                          <Input type="number" data-testid="spot-package-count" value={spotForm.package_count}
                            onChange={(e) => setSpotForm({ ...spotForm, package_count: e.target.value })} placeholder="e.g., 4" />
                        </div>
                        <div>
                          <Label className="text-sm">Gross weight (kg)</Label>
                          <Input type="number" data-testid="spot-weight" value={spotForm.cargo_weight_kg}
                            onChange={(e) => setSpotForm({ ...spotForm, cargo_weight_kg: e.target.value })} placeholder="e.g., 12000" />
                        </div>
                        <div>
                          <Label className="text-sm">Volume (CBM)</Label>
                          <Input type="number" step="0.01" data-testid="spot-cbm" value={spotForm.cargo_cbm}
                            onChange={(e) => setSpotForm({ ...spotForm, cargo_cbm: e.target.value })} placeholder="e.g., 33" />
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-3">
                        <div>
                          <Label className="text-sm">Length (cm)</Label>
                          <Input type="number" value={spotForm.cargo_length_cm}
                            onChange={(e) => setSpotForm({ ...spotForm, cargo_length_cm: e.target.value })} placeholder="120" />
                        </div>
                        <div>
                          <Label className="text-sm">Width (cm)</Label>
                          <Input type="number" value={spotForm.cargo_width_cm}
                            onChange={(e) => setSpotForm({ ...spotForm, cargo_width_cm: e.target.value })} placeholder="80" />
                        </div>
                        <div>
                          <Label className="text-sm">Height (cm)</Label>
                          <Input type="number" value={spotForm.cargo_height_cm}
                            onChange={(e) => setSpotForm({ ...spotForm, cargo_height_cm: e.target.value })} placeholder="100" />
                        </div>
                        <div>
                          <Label className="text-sm">LDM (loading m)</Label>
                          <Input type="number" step="0.1" data-testid="spot-ldm" value={spotForm.ldm || ""}
                            onChange={(e) => setSpotForm({ ...spotForm, ldm: e.target.value })} placeholder="13.6" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm">Temperature min (°C)</Label>
                          <Input type="number" data-testid="spot-temp-min" value={spotForm.temp_min}
                            onChange={(e) => setSpotForm({ ...spotForm, temp_min: e.target.value })} placeholder="e.g., 2" />
                        </div>
                        <div>
                          <Label className="text-sm">Temperature max (°C)</Label>
                          <Input type="number" data-testid="spot-temp-max" value={spotForm.temp_max}
                            onChange={(e) => setSpotForm({ ...spotForm, temp_max: e.target.value })} placeholder="e.g., 8" />
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4">
                        <label className="flex items-center gap-2 text-sm text-amber-800">
                          <input type="checkbox" data-testid="spot-stackable" checked={!!spotForm.stackable}
                            onChange={(e) => setSpotForm({ ...spotForm, stackable: e.target.checked })} />
                          Stackable
                        </label>
                        <label className="flex items-center gap-2 text-sm text-amber-800">
                          <input type="checkbox" data-testid="spot-adr" checked={!!spotForm.adr}
                            onChange={(e) => setSpotForm({ ...spotForm, adr: e.target.checked })} />
                          ADR / Dangerous goods
                        </label>
                        {spotForm.adr && (
                          <Input value={spotForm.adr_class || ""} onChange={(e) => setSpotForm({ ...spotForm, adr_class: e.target.value })}
                            placeholder="ADR class (e.g. 3, UN1203)" className="flex-1 min-w-[160px] h-9" />
                        )}
                      </div>

                      {spotForm.cargo_length_cm && spotForm.cargo_width_cm && spotForm.cargo_height_cm && (
                        <p className="text-xs text-amber-700">
                          Calculated CBM: {((spotForm.cargo_length_cm * spotForm.cargo_width_cm * spotForm.cargo_height_cm) / 1000000 * (spotForm.package_count || 1)).toFixed(3)} m³
                          {spotForm.package_count > 1 && ` (${spotForm.package_count} packages)`}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <Label>Additional Notes</Label>
                      <Textarea
                        value={spotForm.notes}
                        onChange={(e) => setSpotForm({ ...spotForm, notes: e.target.value })}
                        placeholder="Any special requirements..."
                        rows={2}
                      />
                    </div>
                  </div>
                </div>

                {/* Historical Rates Reference */}
                {spotHistoricalRates.length > 0 && (
                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <h4 className="font-medium text-green-800 flex items-center gap-2 mb-3">
                      <TrendingDown className="h-4 w-4" />
                      Historical Rates for Similar Routes
                    </h4>
                    <div className="space-y-2">
                      {spotHistoricalRates.slice(0, 5).map((rate, idx) => (
                        <div key={idx} className="flex justify-between text-sm bg-white/50 p-2 rounded">
                          <div>
                            <span className="text-green-700 font-medium">{rate.trucking_company}</span>
                            <span className="text-xs text-green-600 ml-2">
                              {rate.from} → {rate.to}
                            </span>
                          </div>
                          <span className="font-semibold text-green-900">€{rate.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Trucking Companies Selection */}
              <div className="bg-card rounded-xl p-6 border shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-orange-600" />
                    Select Trucking Companies
                  </h2>
                  <div className="flex items-center gap-2">
                    {matchedCompanies.length > 0 && (
                      <label className="flex items-center gap-1.5 text-sm font-medium text-slate-600 cursor-pointer" data-testid="spot-req-select-all-label">
                        <input type="checkbox" checked={allSpotCompaniesSelected} onChange={toggleSelectAllSpotCompanies} data-testid="spot-req-select-all" />
                        {allSpotCompaniesSelected ? "Deselect all" : "Select all"}
                      </label>
                    )}
                    {selectedCompanies.length > 0 && (
                      <span className="text-sm bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        {selectedCompanies.length} selected
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Sort Controls */}
                {matchedCompanies.length > 0 && (
                  <div className="flex items-center gap-2 mb-4 text-sm">
                    <span className="text-muted-foreground">Sort by:</span>
                    <select
                      value={companySortBy}
                      onChange={(e) => setCompanySortBy(e.target.value)}
                      className="h-8 rounded border px-2 text-sm"
                    >
                      <option value="route_count">Experience (Routes)</option>
                      <option value="avg_price">Avg Price</option>
                      <option value="name">Name</option>
                    </select>
                    <button
                      onClick={() => setCompanySortOrder(companySortOrder === "asc" ? "desc" : "asc")}
                      className="h-8 px-2 rounded border hover:bg-slate-50"
                    >
                      {companySortOrder === "asc" ? "↑ Asc" : "↓ Desc"}
                    </button>
                    <span className="text-muted-foreground ml-2">
                      ({matchedCompanies.length} companies for {spotForm.service_type})
                    </span>
                  </div>
                )}
                
                {loadingCompanies ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
                    <p className="text-sm text-muted-foreground mt-2">Finding companies...</p>
                  </div>
                ) : matchedCompanies.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Enter from/to locations to find matching companies</p>
                    <p className="text-sm">Companies with {spotForm.service_type} experience will be shown</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {sortedCompanies.map((company) => (
                      <div
                        key={company.id}
                        onClick={() => toggleCompanySelection(company.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedCompanies.includes(company.id)
                            ? "border-blue-500 bg-blue-50"
                            : "border-border hover:border-blue-300"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`h-5 w-5 rounded border-2 flex items-center justify-center ${
                            selectedCompanies.includes(company.id)
                              ? "bg-blue-500 border-blue-500"
                              : "border-slate-300"
                          }`}>
                            {selectedCompanies.includes(company.id) && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{company.name}</span>
                              {company.route_matched && (
                                <span className="text-[10px] font-semibold uppercase text-green-700 bg-green-100 px-1.5 py-0.5 rounded" title={company.match_reason || "Serves this route"}>Recommended</span>
                              )}
                              {company.is_preferred && (
                                <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" title="Preferred (10+ orders)" />
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <span>{company.route_count || 0} similar routes</span>
                              {company.avg_price && (
                                <span className="text-green-600 font-medium">Avg: €{company.avg_price}</span>
                              )}
                              {company.last_price && (
                                <span>Last: €{company.last_price}</span>
                              )}
                              {company.service_types?.length > 0 && (
                                <span className="text-blue-600">{company.service_types.join(", ")}</span>
                              )}
                            </div>
                          </div>
                          {company.email && (
                            <Mail className="h-4 w-4 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Three send options */}
                <div className="mt-4 space-y-2">
                  <Button
                    onClick={postLoadToMarketplaces}
                    disabled={postingLoad || !spotForm.from_location || !spotForm.to_location}
                    className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    variant="outline"
                    data-testid="spot-send-marketplaces-btn"
                    title="Post this load to Transporeon and TimoCom only"
                  >
                    {postingLoad ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Truck className="h-4 w-4 mr-2" />}
                    Transporeon + TimoCom
                    <span className={`ml-2 text-[10px] uppercase rounded px-1.5 py-0.5 ${transporeon.configured ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"}`}>TP {transporeon.configured ? "on" : "setup"}</span>
                    <span className={`ml-1 text-[10px] uppercase rounded px-1.5 py-0.5 ${timocom.configured ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-500"}`}>TC {timocom.configured ? "on" : "setup"}</span>
                  </Button>

                  <Button
                    onClick={sendToMarketplacesAndVendors}
                    disabled={postingLoad || sendingSpot || !spotForm.from_location || !spotForm.to_location}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="spot-send-marketplaces-vendors-btn"
                    title="Post to Transporeon/TimoCom AND email the selected truck vendors"
                  >
                    {(postingLoad || sendingSpot) ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    Transporeon + Truck vendors ({selectedCompanies.length})
                  </Button>

                  <Button
                    onClick={sendSpotRequest}
                    disabled={sendingSpot || selectedCompanies.length === 0}
                    className="w-full"
                    variant="outline"
                    data-testid="spot-send-vendors-btn"
                    title="Email the selected truck vendors only"
                  >
                    {sendingSpot ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                    Truck vendors – email ({selectedCompanies.length})
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ============ TENDERING TAB ============ */}
          <TabsContent value="tendering">
            <div className="space-y-6">
              {/* AI Tender Suggestions Panel */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-5 border border-purple-200">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                      <Zap className="h-5 w-5" />
                      Smart Tender Suggestions
                    </h3>
                    <p className="text-sm text-purple-600">
                      Based on your historical transport orders and quote requests
                    </p>
                  </div>
                  <Button 
                    variant={showSuggestions ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setShowSuggestions(!showSuggestions);
                      if (!tenderSuggestions) fetchTenderSuggestions();
                    }}
                  >
                    {loadingSuggestions ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <TrendingUp className="h-4 w-4 mr-2" />
                    )}
                    {showSuggestions ? "Hide Suggestions" : "View Suggestions"}
                  </Button>
                </div>
                
                {showSuggestions && tenderSuggestions && (
                  <div className="space-y-4">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-white rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-purple-600">{tenderSuggestions.summary?.high_priority || 0}</p>
                        <p className="text-xs text-slate-600">High Priority Routes</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-blue-600">{tenderSuggestions.summary?.total_historical_orders || 0}</p>
                        <p className="text-xs text-slate-600">Historical Orders</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center">
                        <p className="text-2xl font-bold text-green-600">{tenderSuggestions.summary?.total_quote_requests || 0}</p>
                        <p className="text-xs text-slate-600">Quote Requests</p>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center">
                        <p className="text-sm font-semibold text-slate-800">{tenderSuggestions.recommended_tender_quarter}</p>
                        <p className="text-xs text-slate-600">Recommended Quarter</p>
                      </div>
                    </div>
                    
                    {/* Top Suggestions */}
                    <div className="bg-white rounded-lg p-4 border">
                      <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                        <Star className="h-4 w-4 text-yellow-500" />
                        Recommended Routes for Next Tender
                      </h4>
                      <div className="space-y-2">
                        {tenderSuggestions.suggestions?.slice(0, 6).map((s, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 rounded bg-slate-50 hover:bg-slate-100">
                            <div className="flex items-center gap-3">
                              <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                s.demand_score >= 50 ? "bg-red-100 text-red-700" :
                                s.demand_score >= 20 ? "bg-amber-100 text-amber-700" :
                                "bg-slate-100 text-slate-700"
                              }`}>
                                {idx + 1}
                              </span>
                              <div>
                                <p className="font-medium text-sm">{s.route}</p>
                                <p className="text-xs text-muted-foreground">
                                  {s.order_count > 0 && `${s.order_count} orders`}
                                  {s.order_count > 0 && s.quote_count > 0 && " + "}
                                  {s.quote_count > 0 && `${s.quote_count} quotes`}
                                  {s.avg_price && ` • Avg €${s.avg_price}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                s.source === "transport_orders" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                              }`}>
                                {s.service_type}
                              </span>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => createTenderFromSuggestion(s)}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Create
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {tenderSuggestions.emerging_demand?.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h5 className="text-xs font-medium text-green-700 mb-2 flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            Emerging Demand (from quotes without orders yet)
                          </h5>
                          <div className="flex flex-wrap gap-2">
                            {tenderSuggestions.emerging_demand.map((s, idx) => (
                              <span key={idx} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                                {s.route} ({s.quote_count} quotes)
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Create Tender Button */}
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold">Active Tenders</h2>
                  <p className="text-sm text-muted-foreground">
                    Manage multi-round pricing tenders with trucking companies
                  </p>
                </div>
                <Button onClick={() => setShowCreateTender(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Tender
                </Button>
              </div>

              {/* Tender List */}
              {loadingTenders ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
                </div>
              ) : tenders.length === 0 ? (
                <div className="bg-card rounded-xl p-12 border text-center">
                  <FileText className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="text-lg font-medium text-muted-foreground mb-2">
                    No Active Tenders
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Create a tender to request competitive pricing from multiple trucking companies
                  </p>
                  <Button onClick={() => setShowCreateTender(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create First Tender
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {tenders.map((tender) => (
                    <div key={tender.id} className="bg-card rounded-xl p-6 border shadow-sm">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-lg">{tender.name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              tender.stage === "rfi" ? "bg-blue-100 text-blue-700" :
                              tender.stage === "round_1" ? "bg-amber-100 text-amber-700" :
                              tender.stage === "round_2" ? "bg-orange-100 text-orange-700" :
                              tender.stage === "completed" ? "bg-green-100 text-green-700" :
                              "bg-slate-100 text-slate-700"
                            }`}>
                              {tender.stage === "rfi" ? "RFI" :
                               tender.stage === "round_1" ? "Round 1" :
                               tender.stage === "round_2" ? "Round 2" :
                               tender.stage === "completed" ? "Completed" : tender.stage}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground">{tender.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            Deadline: {new Date(tender.deadline).toLocaleDateString()}
                          </p>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            <Users className="h-4 w-4" />
                            {tender.responses_count || 0} responses
                          </p>
                        </div>
                      </div>
                      
                      {/* Routes */}
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-xs text-muted-foreground mb-2">ROUTES ({tender.routes?.length || 0})</p>
                        <div className="flex flex-wrap gap-2">
                          {tender.routes?.slice(0, 5).map((route, idx) => (
                            <span key={idx} className="text-xs bg-slate-100 px-2 py-1 rounded">
                              {route.from} → {route.to}
                              {(route.service_type || route.truck_type) && (
                                <span className="ml-1 text-slate-400">
                                  ({[route.service_type, route.truck_type].filter(Boolean).join(" · ")})
                                </span>
                              )}
                            </span>
                          ))}
                          {tender.routes?.length > 5 && (
                            <span className="text-xs text-muted-foreground">
                              +{tender.routes.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="mt-4 flex gap-2 flex-wrap">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setSelectedTender(tender)}
                          data-testid={`view-tender-${tender.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Details
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => downloadExistingTenderTemplate(tender.id)}
                          data-testid={`download-tender-template-${tender.id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download Template
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEditTender(tender)}
                          data-testid={`edit-tender-${tender.id}`}
                        >
                          <Pencil className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          onClick={() => deleteTender(tender.id)}
                          data-testid={`delete-tender-${tender.id}`}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                        {tender.stage !== "completed" && (
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => advanceTenderStage(tender.id)}
                          >
                            Advance to {tender.stage === "rfi" ? "Round 1" : 
                                        tender.stage === "round_1" ? "Round 2" : "Complete"}
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        )}
                      </div>
                      
                      {/* Owner Info */}
                      <div className="mt-3 pt-3 border-t flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Owner: {tender.owner_name || "N/A"}</span>
                        <span>|</span>
                        <span>{tender.owner_email}</span>
                        {tender.contract_period && (
                          <>
                            <span>|</span>
                            <span>Period: {tender.contract_period}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Tender Dialog */}
      <Dialog open={!!editTender} onOpenChange={(open) => { if (!open) setEditTender(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Tender</DialogTitle>
          </DialogHeader>
          {editTender && (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium">Tender Name</label>
                <Input
                  value={editTender.name}
                  onChange={(e) => setEditTender({ ...editTender, name: e.target.value })}
                  data-testid="edit-tender-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">Deadline</label>
                  <Input
                    type="date"
                    value={editTender.deadline}
                    onChange={(e) => setEditTender({ ...editTender, deadline: e.target.value })}
                    data-testid="edit-tender-deadline"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Contract Period</label>
                  <Input
                    value={editTender.contract_period}
                    onChange={(e) => setEditTender({ ...editTender, contract_period: e.target.value })}
                    placeholder="e.g. Q4 2026 - Q1 2027"
                    data-testid="edit-tender-period"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  value={editTender.description}
                  onChange={(e) => setEditTender({ ...editTender, description: e.target.value })}
                  rows={2}
                  data-testid="edit-tender-description"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Requirements</label>
                <Textarea
                  value={editTender.requirements}
                  onChange={(e) => setEditTender({ ...editTender, requirements: e.target.value })}
                  rows={2}
                  data-testid="edit-tender-requirements"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditTender(null)}>Cancel</Button>
                <Button onClick={saveEditTender} data-testid="save-edit-tender">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Tender Dialog - Step-by-step workflow */}
      <Dialog open={showCreateTender} onOpenChange={(open) => {
        setShowCreateTender(open);
        if (!open) {
          setTenderTemplateUploaded(false);
          setSelectedTenderCompanies([]);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Tender</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Step 1: Upload Template or Enter Basic Info */}
            <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border">
              <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-3">
                <span className="h-6 w-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">1</span>
                Define Your Routes
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Download our template, fill in your routes and requirements, then upload it to populate the tender.
              </p>
              
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={downloadTenderTemplate}>
                  <Download className="h-4 w-4 mr-2" />
                  Download Template
                </Button>
                
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleTenderTemplateUpload}
                    className="hidden"
                  />
                  <Button variant={tenderTemplateUploaded ? "default" : "outline"} as="span">
                    <Upload className="h-4 w-4 mr-2" />
                    {tenderTemplateUploaded ? `${tenderForm.routes.length} Routes Loaded` : "Upload Filled Template"}
                  </Button>
                </label>
                
                {tenderTemplateUploaded && (
                  <span className="text-green-600 text-sm flex items-center gap-1">
                    <Check className="h-4 w-4" />
                    Template uploaded
                  </span>
                )}
              </div>
              
              {/* Show imported routes summary */}
              {tenderTemplateUploaded && tenderForm.routes.length > 0 && (
                <div className="mt-4 p-3 bg-white rounded-lg border max-h-[150px] overflow-y-auto">
                  <p className="text-xs font-medium text-slate-600 mb-2">Imported Routes:</p>
                  <div className="space-y-1">
                    {tenderForm.routes.slice(0, 10).map((route, idx) => (
                      <div key={idx} className="text-xs flex items-center gap-2">
                        <span className="text-slate-400">{route.route_id || idx + 1}.</span>
                        <span>{route.from}</span>
                        <ChevronRight className="h-3 w-3 text-slate-400" />
                        <span>{route.to}</span>
                        <span className="text-blue-600">({route.service_type})</span>
                      </div>
                    ))}
                    {tenderForm.routes.length > 10 && (
                      <p className="text-xs text-slate-500">...and {tenderForm.routes.length - 10} more routes</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Step 2: Basic Tender Info */}
            <div className="bg-slate-50 rounded-xl p-5 border">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-4">
                <span className="h-6 w-6 rounded-full bg-slate-600 text-white text-sm flex items-center justify-center">2</span>
                Tender Details
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tender Name *</Label>
                  <Input
                    value={tenderForm.name}
                    onChange={(e) => setTenderForm({ ...tenderForm, name: e.target.value })}
                    placeholder="e.g., Q2 2026 Koper-Germany Routes"
                  />
                </div>
                <div>
                  <Label>Deadline *</Label>
                  <Input
                    type="date"
                    value={tenderForm.deadline}
                    onChange={(e) => setTenderForm({ ...tenderForm, deadline: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Contract Period</Label>
                  <Input
                    value={tenderForm.contract_period}
                    onChange={(e) => setTenderForm({ ...tenderForm, contract_period: e.target.value })}
                    placeholder="e.g., Q2 2026 - Q1 2027"
                  />
                </div>
                <div>
                  <Label>Owner Email</Label>
                  <Input
                    type="email"
                    value={tenderForm.owner_email}
                    onChange={(e) => setTenderForm({ ...tenderForm, owner_email: e.target.value })}
                    placeholder="your.email@europacific.com"
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <Label>Description & Requirements</Label>
                <Textarea
                  value={tenderForm.description}
                  onChange={(e) => setTenderForm({ ...tenderForm, description: e.target.value })}
                  placeholder="Describe the tender requirements, expected volumes, service expectations, GPS tracking, etc..."
                  rows={3}
                />
              </div>
            </div>
            
            {/* Step 3: Filter & Select Companies */}
            <div className="bg-orange-50 rounded-xl p-5 border border-orange-200">
              <h3 className="font-semibold text-orange-800 flex items-center gap-2 mb-4">
                <span className="h-6 w-6 rounded-full bg-orange-600 text-white text-sm flex items-center justify-center">3</span>
                Select Trucking Companies to Invite
              </h3>
              
              {/* Filters */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                <div>
                  <Label className="text-xs">Loading Country</Label>
                  <Input
                    value={tenderCompanyFilters.country_loading}
                    onChange={(e) => setTenderCompanyFilters({ ...tenderCompanyFilters, country_loading: e.target.value })}
                    placeholder="e.g., Slovenia"
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Delivery Country</Label>
                  <Input
                    value={tenderCompanyFilters.country_delivery}
                    onChange={(e) => setTenderCompanyFilters({ ...tenderCompanyFilters, country_delivery: e.target.value })}
                    placeholder="e.g., Germany"
                    className="h-9"
                  />
                </div>
                <div>
                  <Label className="text-xs">Service Type</Label>
                  <select
                    value={tenderCompanyFilters.service_type}
                    onChange={(e) => setTenderCompanyFilters({ ...tenderCompanyFilters, service_type: e.target.value })}
                    className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    <option value="all">All Types</option>
                    <option value="FCL">FCL</option>
                    <option value="FTL">FTL</option>
                    <option value="LTL">LTL</option>
                    <option value="OOG">OOG</option>
                    <option value="Express">Express</option>
                    <option value="Refrigerated">Reefer</option>
                    <option value="Hazmat">Hazmat</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Search Company</Label>
                  <Input
                    value={tenderCompanyFilters.search_name}
                    onChange={(e) => setTenderCompanyFilters({ ...tenderCompanyFilters, search_name: e.target.value })}
                    placeholder="Company name..."
                    className="h-9"
                  />
                </div>
              </div>
              
              {/* Selection Actions */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={selectAllFilteredCompanies}>
                    <Check className="h-3 w-3 mr-1" />
                    Select All ({availableCompaniesForTender.length})
                  </Button>
                  <Button variant="ghost" size="sm" onClick={clearCompanySelection}>
                    <X className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                </div>
                <span className="text-sm font-medium text-orange-700">
                  {selectedTenderCompanies.length} companies selected
                </span>
              </div>
              
              {/* Company List */}
              <div className="border rounded-lg bg-white max-h-[200px] overflow-y-auto">
                {loadingTenderCompanies ? (
                  <div className="p-4 text-center">
                    <Loader2 className="h-6 w-6 mx-auto animate-spin text-orange-500" />
                  </div>
                ) : availableCompaniesForTender.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No companies found. Adjust filters to see more.
                  </div>
                ) : (
                  availableCompaniesForTender.map((company) => (
                    <div
                      key={company.id}
                      onClick={() => toggleTenderCompanySelection(company.id)}
                      className={`p-2 border-b last:border-b-0 cursor-pointer hover:bg-orange-50 flex items-center gap-2 ${
                        selectedTenderCompanies.includes(company.id) ? "bg-orange-100" : ""
                      }`}
                    >
                      <div className={`h-4 w-4 rounded border flex items-center justify-center ${
                        selectedTenderCompanies.includes(company.id) ? "bg-orange-500 border-orange-500" : "border-slate-300"
                      }`}>
                        {selectedTenderCompanies.includes(company.id) && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="font-medium text-sm">{company.name}</span>
                        {company.email && (
                          <span className="text-xs text-slate-500 ml-2">{company.email}</span>
                        )}
                        {company.route_count > 0 && (
                          <span className="text-xs text-muted-foreground ml-2">
                            ({company.route_count} routes, avg €{company.avg_price || "N/A"})
                          </span>
                        )}
                      </div>
                      {company.service_types?.length > 0 && (
                        <span className="text-xs text-orange-600">{company.service_types.slice(0, 2).join(", ")}</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Step 4: Tender Process Stages */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
              <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-4">
                <span className="h-6 w-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">4</span>
                Tender Process & Dates
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked disabled className="h-4 w-4" />
                  <span className="font-medium text-sm text-blue-800 flex-1">RFI Stage</span>
                  <Input type="date" value={tenderForm.process_stages.rfi.date}
                    onChange={(e) => updateProcessStage("rfi", "date", e.target.value)}
                    className="w-40 h-8 text-sm" />
                </div>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={tenderForm.process_stages.round_1.enabled}
                    onChange={(e) => updateProcessStage("round_1", "enabled", e.target.checked)}
                    className="h-4 w-4" />
                  <span className={`font-medium text-sm flex-1 ${tenderForm.process_stages.round_1.enabled ? "text-blue-800" : "text-slate-400"}`}>
                    Round 1 (Optional)
                  </span>
                  <Input type="date" value={tenderForm.process_stages.round_1.date}
                    onChange={(e) => updateProcessStage("round_1", "date", e.target.value)}
                    disabled={!tenderForm.process_stages.round_1.enabled}
                    className="w-40 h-8 text-sm" />
                </div>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={tenderForm.process_stages.round_2.enabled}
                    onChange={(e) => updateProcessStage("round_2", "enabled", e.target.checked)}
                    className="h-4 w-4" />
                  <span className={`font-medium text-sm flex-1 ${tenderForm.process_stages.round_2.enabled ? "text-blue-800" : "text-slate-400"}`}>
                    Round 2 (Optional)
                  </span>
                  <Input type="date" value={tenderForm.process_stages.round_2.date}
                    onChange={(e) => updateProcessStage("round_2", "date", e.target.value)}
                    disabled={!tenderForm.process_stages.round_2.enabled}
                    className="w-40 h-8 text-sm" />
                </div>
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked disabled className="h-4 w-4" />
                  <span className="font-medium text-sm text-blue-800 flex-1">Award Stage</span>
                  <Input type="date" value={tenderForm.process_stages.award.date}
                    onChange={(e) => updateProcessStage("award", "date", e.target.value)}
                    className="w-40 h-8 text-sm" />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTender(false)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={createTenderViaTransporeon}
              disabled={sendingTp || !tenderForm.name || !tenderForm.deadline}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="tender-transporeon-btn"
              title="Publish this tender to carriers via Transporeon"
            >
              {sendingTp ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Truck className="h-4 w-4 mr-2" />}
              Publish to Transporeon
              <span className={`ml-2 text-[10px] uppercase rounded px-1.5 py-0.5 ${transporeon.configured ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"}`}>{transporeon.configured ? transporeon.environment : "setup"}</span>
            </Button>
            <Button 
              onClick={createTender}
              disabled={!tenderForm.name || !tenderForm.deadline || selectedTenderCompanies.length === 0}
            >
              <Send className="h-4 w-4 mr-2" />
              Create Tender & Send ({selectedTenderCompanies.length} invites)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tender Detail Dialog */}
      <Dialog open={!!selectedTender} onOpenChange={() => setSelectedTender(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedTender?.name}
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                selectedTender?.stage === "rfi" ? "bg-blue-100 text-blue-700" :
                selectedTender?.stage === "round_1" ? "bg-amber-100 text-amber-700" :
                selectedTender?.stage === "round_2" ? "bg-orange-100 text-orange-700" :
                "bg-green-100 text-green-700"
              }`}>
                {selectedTender?.stage?.replace('_', ' ').toUpperCase()}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          {selectedTender && (
            <div className="space-y-6 py-4">
              {/* Tender Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Owner</p>
                  <p className="font-medium">{selectedTender.owner_name} ({selectedTender.owner_email})</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Deadline</p>
                  <p className="font-medium">{new Date(selectedTender.deadline).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Contract Period</p>
                  <p className="font-medium">{selectedTender.contract_period || "TBD"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Responses</p>
                  <p className="font-medium">{selectedTender.responses_count || 0} submissions</p>
                </div>
              </div>
              
              {selectedTender.description && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Description</p>
                  <p className="text-sm">{selectedTender.description}</p>
                </div>
              )}
              
              {/* Routes */}
              <div>
                <p className="text-sm text-muted-foreground mb-2">Routes ({selectedTender.routes?.length || 0})</p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2">From</th>
                        <th className="text-left p-2">To</th>
                        <th className="text-left p-2">Type</th>
                        <th className="text-left p-2">Frequency</th>
                        <th className="text-left p-2">Volume</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTender.routes?.map((route, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-2">{route.from}</td>
                          <td className="p-2">{route.to}</td>
                          <td className="p-2">{route.service_type}</td>
                          <td className="p-2">{route.frequency || "-"}</td>
                          <td className="p-2">{route.volume || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Responses/Bids */}
              {selectedTender.responses && selectedTender.responses.length > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Submitted Bids</p>
                  <div className="space-y-3">
                    {selectedTender.responses.map((response, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium">{response.company_name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {response.contact_person} • {response.company_email}
                            </p>
                          </div>
                          <div className="text-right text-sm">
                            <p className="text-muted-foreground">Stage: {response.stage}</p>
                            <p className="text-muted-foreground">{new Date(response.submitted_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        {response.bids && response.bids.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-muted-foreground mb-2">Quoted Rates:</p>
                            <div className="flex flex-wrap gap-2">
                              {response.bids.map((bid, bidIdx) => (
                                <span key={bidIdx} className="text-xs bg-slate-100 px-2 py-1 rounded">
                                  {bid.from} → {bid.to}: {bid.currency} {bid.price}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Invited companies & response tracking */}
              {(selectedTender.invited_companies?.length > 0 || selectedTender.invited_emails?.length > 0) && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Invited Companies & Responses</p>
                  <div className="space-y-1" data-testid="tender-invited-tracking">
                    {(selectedTender.invited_companies?.length ? selectedTender.invited_companies : selectedTender.invited_emails).map((name, idx) => {
                      const email = selectedTender.invited_emails?.[idx];
                      const responded = (selectedTender.responses || []).some((r) =>
                        (email && r.company_email && r.company_email.toLowerCase() === String(email).toLowerCase()) ||
                        (r.company_name && name && r.company_name.toLowerCase() === String(name).toLowerCase())
                      );
                      return (
                        <div key={idx} className="flex items-center justify-between text-sm border rounded px-3 py-1.5">
                          <span>
                            {name}
                            {email ? <span className="text-muted-foreground text-xs ml-1">({email})</span> : null}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${responded ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                            {responded ? "Responded" : "Awaiting"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => downloadExistingTenderTemplate(selectedTender.id)}>
                  <Download className="h-4 w-4 mr-2" />
                  Download XLS Template
                </Button>
                {selectedTender.stage !== "completed" && (
                  <Button onClick={() => advanceTenderStage(selectedTender.id)}>
                    Advance to Next Stage
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </div>
              
              {/* Public Link */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-2">Public Submission Link (share with trucking companies):</p>
                <code className="text-xs bg-white p-2 rounded border block break-all">
                  {window.location.origin}/tender-bid/{selectedTender.id}/{selectedTender.public_token}
                </code>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transporeon Offers Dialog */}
      <Dialog open={!!tpOffers} onOpenChange={() => setTpOffers(null)}>
        <DialogContent className="max-w-2xl" data-testid="transporeon-offers-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-indigo-600" />
              Transporeon {tpOffers?.kind === "tender" ? "Tender" : "Spot"} — Carrier Offers
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <TransporeonQuotesPanel
              apiUrl={API_URL}
              token={token}
              transportNumber={tpOffers?.transport_number}
              onConvert={convertOfferToOrder}
              convertingId={convertingTp}
              autoLoad={true}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Marketplace Rates Dialog — Transporeon + TimoCom side by side */}
      <Dialog open={!!marketplace} onOpenChange={() => setMarketplace(null)}>
        <DialogContent className="max-w-2xl" data-testid="marketplace-rates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-indigo-600" />
              Truck Rates — Transporeon + TimoCom
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <div className="flex flex-wrap gap-2">
                {marketplace?.transporeon_ref && (
                  <span className="text-xs bg-indigo-50 text-indigo-700 rounded px-2 py-1" data-testid="marketplace-tp-ref">
                    Transporeon: <span className="font-mono">{marketplace.transporeon_ref}</span>
                  </span>
                )}
                {marketplace?.timocom_ref && (
                  <span className="text-xs bg-orange-50 text-orange-700 rounded px-2 py-1" data-testid="marketplace-tc-ref">
                    TimoCom: <span className="font-mono">{marketplace.timocom_ref}</span>
                  </span>
                )}
                {!marketplace?.transporeon_ref && !marketplace?.timocom_ref && (
                  <span className="text-xs text-muted-foreground">No platform posted — add credentials in Settings.</span>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={() => fetchMarketplaceRates(false)} disabled={marketplace?.loading} data-testid="marketplace-refresh-rates-btn">
                {marketplace?.loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Refresh rates
              </Button>
            </div>

            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <label className="flex items-center gap-1.5 cursor-pointer select-none" data-testid="marketplace-autorefresh-toggle">
                <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="h-3 w-3 accent-indigo-600" />
                Auto-refresh every 3 min
              </label>
              {marketplace?.lastUpdated && (
                <span data-testid="marketplace-last-updated">Updated {marketplace.lastUpdated.toLocaleTimeString()}</span>
              )}
            </div>

            <div className="flex flex-wrap gap-x-2 text-xs" data-testid="marketplace-provider-status">
              {marketplace?.providers?.transporeon && (
                <span className={marketplace.providers.transporeon.ok ? "text-green-600" : "text-slate-500"}>
                  Transporeon: {marketplace.providers.transporeon.ok ? "posted" : (marketplace.providers.transporeon.status === 503 ? "setup needed" : "unavailable")}
                </span>
              )}
              {marketplace?.providers?.timocom && (
                <span className={marketplace.providers.timocom.ok ? "text-green-600" : "text-slate-500"}>
                  · TimoCom: {marketplace.providers.timocom.ok ? "posted" : (marketplace.providers.timocom.status === 503 ? "setup needed" : "unavailable")}
                </span>
              )}
            </div>

            {(!marketplace?.rates || marketplace.rates.length === 0) ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                Load posted to the marketplaces. Click <b>Refresh rates</b> to pull carrier offers from both platforms as they come in.
              </div>
            ) : (
              <div className="space-y-2" data-testid="marketplace-rates-list">
                {marketplace.rates.map((r, i) => {
                  const isBest = marketplace.best && r.price != null && r.price === marketplace.best.price;
                  return (
                  <div key={`${r.source}-${r.offer_id || i}`} className={`flex items-center justify-between border rounded-lg p-3 ${isBest ? "border-green-400 bg-green-50/70 ring-1 ring-green-300" : ""}`} data-testid={`marketplace-rate-${i}`}>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase rounded px-1.5 py-0.5 ${r.source === "transporeon" ? "bg-indigo-100 text-indigo-700" : "bg-orange-100 text-orange-700"}`}>
                          {r.source === "transporeon" ? "Transporeon" : "TimoCom"}
                        </span>
                        <span className="font-medium text-slate-800">{r.carrier || "Carrier"}</span>
                        {isBest && (
                          <span className="text-[10px] uppercase rounded px-1.5 py-0.5 bg-green-100 text-green-700 font-semibold" data-testid={`marketplace-best-badge-${i}`}>
                            ★ Best price
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {r.price != null ? <span className={`font-semibold ${isBest ? "text-green-700" : "text-slate-700"}`}>{r.price} {r.currency || ""}</span> : "No price"}
                        {r.valid_until ? <span className="ml-2">· valid until {r.valid_until}</span> : null}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={isBest ? "default" : "outline"}
                      onClick={() => convertRateToOrder(r)}
                      disabled={convertingRate === `${r.source}-${r.offer_id || r.carrier}`}
                      data-testid={`marketplace-accept-rate-${i}`}
                      title="Create a Transport Order from this rate and close the request on both platforms"
                    >
                      {convertingRate === `${r.source}-${r.offer_id || r.carrier}` ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                      Create TO
                    </Button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

